import { MutableRefObject } from "react";
import { BrandEnum } from "../_common/enum/brand.enum";
import { TrackingStageEnum } from "../_common/enum/trackingStage.enum";
import { IQuoteConversionAuditManage } from "../_common/interface/quoteConversionAudit/iQuoteConversionAudit.interface";
import { IQuoteTrackingDetail } from "../_common/interface/quoteTrackingDetail/iQuoteTrackingDetail.interface";
import { quoteConversionAuditService } from "../_services/quoteConversionAudit.service";
import { quoteTracking } from "../_services/quoteTracking.service";
import { getClientEnvironment } from "./clientEnviroment.helper";
import {
    createMessageId,
    getOrCreateTrackingSnapshot,
    getOrCreateVisitId,
    hasConversionBeenSent,
    markConversionAsSent,
} from "./trackingSnapshot.helper";

interface INotifyParentConversionWithAuditParams {
    formId?: string | number | null;
    quoteId?: number | null;
    brandId: BrandEnum | null;
    formTypeId?: string | null;
    activeStep?: number | null;
    eventName?: string | null;
    pageUrl?: string | null;
    sentRef?: MutableRefObject<boolean>;
    parentWindow?: Window | null;
    saveQuoteSubmittedTracking?: boolean;
    queryParams?: URLSearchParams | null;
}

function normalize(v: string | null): string {
    if (!v) return "";
    const t = v.trim();
    if (!t) return "";
    if (t.toLowerCase() === "unknown") return "";
    if (t.toLowerCase() === "(not set)") return "";
    return t;
}

function getUtmFromLandingPage(handlLandingPage: string, key: string): string {
    if (!handlLandingPage) return "";

    try {
        const url = new URL(handlLandingPage);
        const value = url.searchParams.get(key);
        if (!value) return "";
        if (value === "unknown" || value === "(not set)") return "";
        return value;
    } catch {
        return "";
    }
}

function buildQuoteTrackingDetailInline(params: {
    formId: number;
    brandId: BrandEnum | null;
    formTypeId?: string | null;
    quoteId?: number | null;
    trackingStageId: TrackingStageEnum;
    queryParams: URLSearchParams;
    visitId: string;
}): IQuoteTrackingDetail {
    const qp = params.queryParams;

    const utmSource = normalize(qp.get("utm_source")) || getUtmFromLandingPage(normalize(qp.get("handl_landing_page")), "utm_source") || "";
    const utmMedium = normalize(qp.get("utm_medium")) || getUtmFromLandingPage(normalize(qp.get("handl_landing_page")), "utm_medium") || "";
    const utmCampaign = normalize(qp.get("utm_campaign")) || getUtmFromLandingPage(normalize(qp.get("handl_landing_page")), "utm_campaign") || "";
    const utmTerm = normalize(qp.get("utm_term")) || getUtmFromLandingPage(normalize(qp.get("handl_landing_page")), "utm_term") || "";

    const gclid = normalize(qp.get("gclid"));
    const fbclid = normalize(qp.get("fbclid"));
    const fbc = normalize(qp.get("_fbc"));
    const fbp = normalize(qp.get("_fbp"));

    const hasGoogleClick = !!gclid;
    const hasMetaClick = !!fbclid;

    const handlOriginalRef = normalize(qp.get("handl_original_ref"));
    const handlRefDomain = normalize(qp.get("handl_ref_domain"));

    const pysTrafficSource = normalize(qp.get("pysTrafficSource"));
    const lastPysTrafficSource = normalize(qp.get("last_pysTrafficSource"));
    const organicSource = normalize(qp.get("organic_source"));
    const firstTrafficSource = normalize(qp.get("first_traffic_source"));

    const seoEvidence = handlOriginalRef || organicSource;

    const isSearchEngineRef =
        /google\.|bing\.|yahoo\.|duckduckgo\.|baidu\.|yandex\.|search\.apple\.com|spotlight\.apple\.com/i.test(seoEvidence);

    const isSEO =
        !utmSource &&
        !utmMedium &&
        !utmCampaign &&
        !hasGoogleClick &&
        !hasMetaClick &&
        !!seoEvidence &&
        isSearchEngineRef;

    const pys = (pysTrafficSource || lastPysTrafficSource).toLowerCase();

    const inferredSource =
        utmSource ||
        (hasGoogleClick
            ? "google"
            : hasMetaClick
                ? "facebook"
                : isSEO
                    ? "SEO"
                    : pys.includes("facebook") || pys.includes("instagram")
                        ? "facebook"
                        : pys.includes("google")
                            ? "google"
                            : pys.includes("bing")
                                ? "bing"
                                : firstTrafficSource.toLowerCase() === "direct"
                                    ? "direct"
                                    : "UNKNOWN");

    const inferredMedium =
        utmMedium ||
        (hasGoogleClick
            ? "cpc"
            : hasMetaClick
                ? "paid_social"
                : isSEO
                    ? "organic"
                    : pys.includes("paid") || pys.includes("cpc")
                        ? "cpc"
                        : pys.includes("social") || pys.includes("facebook") || pys.includes("instagram")
                            ? "paid_social"
                            : firstTrafficSource.toLowerCase() === "direct"
                                ? "direct"
                                : "UNKNOWN");

    const inferredCampaign =
        utmCampaign ||
        (hasGoogleClick
            ? "google_ads"
            : hasMetaClick
                ? "meta"
                : isSEO
                    ? "SEO"
                    : (pys ? pys : "UNKNOWN"));

    return {
        formId: params.formId,
        brandId: params.brandId,
        formTypeId: params.formTypeId ?? "",
        quoteId: params.quoteId != null ? String(params.quoteId) : "",
        trackingStageId: params.trackingStageId,

        source: inferredSource,
        originalSource: normalize(qp.get("first_utm_source")) || inferredSource,

        medium: inferredMedium,
        originalMedium: normalize(qp.get("first_utm_medium")) || inferredMedium,

        campaign: inferredCampaign,
        originalCampaign: normalize(qp.get("first_utm_campaign")) || (utmCampaign ? utmCampaign : ""),

        keyword: utmTerm || normalize(qp.get("first_utm_term")) || "",
        originalKeyword: normalize(qp.get("first_utm_term")) || (utmTerm ? utmTerm : ""),

        ip: normalize(qp.get("handl_ip")),
        gClId: gclid,

        handlLandingPage: normalize(qp.get("handl_landing_page")),
        handlLandingPageBase: normalize(qp.get("handl_landing_page_base")),
        handlRef: normalize(qp.get("handl_ref")),
        handlOriginalRef,
        handlRefDomain,
        handlUrl: normalize(qp.get("handl_url")),
        handlUrlBase: normalize(qp.get("handl_url_base")),
        handlID: normalize(qp.get("handlID")),

        trafficSource:
            normalize(qp.get("traffic_source")) ||
            pysTrafficSource ||
            (isSEO ? "SEO" : hasMetaClick ? "meta" : hasGoogleClick ? "google_ads" : ""),

        originalReferringUrl: "",

        metaFbc: fbc,
        metaFbp: fbp,
        metaFbclid: fbclid,

        isFormDuplicate: false,
        formSequenceNo: null,
        visitId: params.visitId
    };
}

export async function notifyParentConversionWithAudit(
    params: INotifyParentConversionWithAuditParams
): Promise<void> {
    const numericFormId = params.formId ? Number(params.formId) : null;
    const transactionId = String(params.formId || "");
    if (!transactionId || !numericFormId) return;

    const eventName = params.eventName || "quote_form_submission";
    const messageId = createMessageId();
    const visitId = getOrCreateVisitId();
    const env = getClientEnvironment();

    const auditBase: IQuoteConversionAuditManage = {
        formId: numericFormId,
        quoteId: params.quoteId ?? null,
        brandId: params.brandId ?? null,
        visitId,
        eventName,
        transactionId,
        messageId,
        activeStep: params.activeStep ?? null,
        pageUrl: params.pageUrl || window.location.href,
        deviceType: env.deviceType,
        browser: env.browser,
        operatingSystem: env.operatingSystem,
        userAgent: env.userAgent,
    };

    if (params.saveQuoteSubmittedTracking) {
        try {
            const snapshotParams =
                params.queryParams ??
                getOrCreateTrackingSnapshot(new URLSearchParams(window.location.search));

            const trackingPayload = buildQuoteTrackingDetailInline({
                formId: numericFormId,
                brandId: params.brandId,
                formTypeId: params.formTypeId ?? "",
                quoteId: params.quoteId ?? null,
                trackingStageId: TrackingStageEnum.QuoteSubmitted,
                queryParams: snapshotParams,
                visitId
            });

            await quoteTracking.post(trackingPayload);
        } catch (err: any) {
            console.error("[QuoteTracking] FAILED QuoteSubmitted", {
                message: err?.message,
                status: err?.response?.status,
                data: err?.response?.data,
            });
        }
    }

    const alreadySent =
        !!params.sentRef?.current || hasConversionBeenSent(transactionId);

    if (alreadySent) {
        try {
            await quoteConversionAuditService.post({
                ...auditBase,
                stage: "child_send_blocked",
                isDuplicate: true,
            });
        } catch (err) {
            console.warn("[QuoteConversionAudit] Failed to save child_send_blocked", err);
        }
        return;
    }

    if (params.sentRef) {
        params.sentRef.current = true;
    }

    markConversionAsSent(transactionId);

    try {
        await quoteConversionAuditService.post({
            ...auditBase,
            stage: "child_send_attempt",
            isDuplicate: false,
        });
    } catch (err) {
        console.warn("[QuoteConversionAudit] Failed to save child_send_attempt", err);
    }

    const targetWindow = params.parentWindow || window.parent;
    if (!targetWindow) return;

    targetWindow.postMessage(
        {
            type: "conversion",
            event: eventName,
            transaction_id: transactionId,
            message_id: messageId,
        },
        "*"
    );
}